import { useState, useEffect } from "react";
import { useApp } from "../App";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { BASE_URL } from "../config";
import "../styles/account.css";

import {
  FiLogOut,
  FiPieChart,
  FiPackage,
  FiHeart,
  FiMapPin,
  FiUser,
  FiShoppingCart,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiTruck,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiEye,
  FiEyeOff,
  FiShield,
  FiClock,
  FiAward,
  FiArrowRight,
  FiX,
  FiHome,
  FiBriefcase,
  FiMail,
  FiRefreshCw,
} from "react-icons/fi";

// ─── Order Tracking Progress ─────────────────────────────────────────────────
const ORDER_STEPS = ["Confirmed", "Processing", "Shipped", "Delivered"];

function OrderProgress({ status }) {
  if (status === "Cancelled") {
    return (
      <div className="kkb-track__cancelled">
        <FiX size={15} />
        <span>Order was cancelled</span>
      </div>
    );
  }
  const currentIdx = ORDER_STEPS.indexOf(status);
  return (
    <div className="kkb-track">
      {ORDER_STEPS.map((step, i) => {
        const done = i <= currentIdx;
        const current = i === currentIdx;
        return (
          <div
            key={step}
            className={[
              "kkb-track__step",
              done ? "kkb-track__step--done" : "",
              current ? "kkb-track__step--current" : "",
            ].join(" ")}
          >
            {i < ORDER_STEPS.length - 1 && (
              <div
                className={`kkb-track__line ${i < currentIdx ? "kkb-track__line--done" : ""}`}
              />
            )}
            <div className="kkb-track__dot">
              {done && <FiCheck size={11} />}
            </div>
            <span className="kkb-track__label">{step}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Verification Modal ─────────────────────────────────────────────────────
function VerificationModal({ email, onResend, onBack, loading }) {
  return (
    <div className="kkb-verification-modal">
      <div className="kkb-verification-modal__content">
        <div className="kkb-verification-modal__icon">
          <FiMail size={48} />
        </div>
        <h2>Verify Your Email</h2>
        <p>
          We've sent a verification link to <strong>{email}</strong>
        </p>
        <p>
          Please check your email and click the verification link to complete
          your registration.
        </p>
        <div className="kkb-verification-modal__actions">
          <button
            onClick={onResend}
            className="kkb-btn kkb-btn--ghost"
            disabled={loading}
          >
            <FiRefreshCw size={14} />
            {loading ? "Sending..." : "Resend Email"}
          </button>
          <button onClick={onBack} className="kkb-btn kkb-btn--primary">
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Auth Form ───────────────────────────────────────────────────────────────
function AuthForm() {
  const { login, register, showToast } = useApp();
  const navigate = useNavigate(); // ✅ Add navigate
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [verificationNeeded, setVerificationNeeded] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [lForm, setLForm] = useState({ email: "", password: "" });
  const [rForm, setRForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  // Helper function to safely show toast
  const safeShowToast = (message, type = "success") => {
    if (showToast && typeof showToast === "function") {
      showToast(message, type);
    } else {
      console.log(`Toast (${type}):`, message);
      alert(message); // Fallback to alert if toast isn't available
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(lForm.email, lForm.password);

      // ✅ Check for redirect after login
      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      const intendedAction = sessionStorage.getItem("intendedAction");

      if (redirectPath && intendedAction === "add_to_cart") {
        sessionStorage.removeItem("redirectAfterLogin");
        sessionStorage.removeItem("intendedAction");
        navigate(redirectPath);
      } else {
        safeShowToast("Welcome back! 👋");
      }
    } catch (err) {
      if (err.needsVerification) {
        setVerificationNeeded(true);
        setVerificationEmail(lForm.email);
        safeShowToast(err.message, "info");
      } else {
        safeShowToast(err.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }; 

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.auth.register({
        name: rForm.name,
        email: rForm.email,
        password: rForm.password,
        phone: rForm.phone,
      });

      if (response.needsVerification) {
        setVerificationNeeded(true);
        setVerificationEmail(rForm.email);
        safeShowToast(
          response.message ||
            "Please verify your email to complete registration",
          "success",
        );
      } else if (response.success === false) {
        safeShowToast(response.message || "Registration failed", "error");
      } else {
        safeShowToast(
          "Account created! Please verify your email to login.",
          "success",
        );
        setVerificationNeeded(true);
        setVerificationEmail(rForm.email);
      }
    } catch (err) {
      console.error("Registration error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Registration failed. Please try again.";
      safeShowToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await api.auth.resendVerification(verificationEmail);
      safeShowToast(
        "Verification email resent! Please check your inbox.",
        "success",
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to resend verification email.";
      safeShowToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // If verification is needed, show modal
  if (verificationNeeded) {
    return (
      <VerificationModal
        email={verificationEmail}
        onResend={handleResendVerification}
        onBack={() => {
          setVerificationNeeded(false);
          setVerificationEmail("");
        }}
        loading={loading}
      />
    );
  }

  return (
    <div className="kkb-auth">
      <div className="kkb-auth__container">
        {/* LEFT SIDE IMAGE */}
        <div className="kkb-auth__left">
          <img src="/assets/authbanner.png" alt="Lungi Collection" />
        </div>

        {/* RIGHT SIDE FORM */}
        <div className="kkb-auth__box">
          <div className="kkb-auth__logo">KKB & Co.</div>

          <div className="kkb-auth__tabs">
            <button
              className={`kkb-auth__tab ${tab === "login" ? "kkb-auth__tab--active" : ""}`}
              onClick={() => setTab("login")}
            >
              Sign In
            </button>
            <button
              className={`kkb-auth__tab ${tab === "register" ? "kkb-auth__tab--active" : ""}`}
              onClick={() => setTab("register")}
            >
              Register
            </button>
          </div>

          {tab === "login" ? (
            <form onSubmit={handleLogin} className="kkb-auth__form">
              <h2 className="kkb-auth__title">Welcome back</h2>
              <p className="kkb-auth__subtitle">Sign in to continue shopping</p>
              <div className="kkb-form__group">
                <label className="kkb-form__label">Email Address</label>
                <input
                  className="kkb-form__input"
                  type="email"
                  value={lForm.email}
                  onChange={(e) =>
                    setLForm((p) => ({ ...p, email: e.target.value }))
                  }
                  placeholder="you@email.com"
                  required
                />
              </div>

              <div className="kkb-form__group">
                <label className="kkb-form__label">Password</label>
                <div className="kkb-form__input-wrap">
                  <input
                    className="kkb-form__input"
                    type={showPw ? "text" : "password"}
                    value={lForm.password}
                    onChange={(e) =>
                      setLForm((p) => ({ ...p, password: e.target.value }))
                    }
                    placeholder="Your password"
                    required
                  />
                  <button
                    type="button"
                    className="kkb-form__eye"
                    onClick={() => setShowPw((p) => !p)}
                  >
                    {showPw ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                <Link
                  to="/forgot-password"
                  className="kkb-auth__forgot"
                  style={{
                    fontSize: "13px",
                    color: "#8B6914",
                    textDecoration: "none",
                    display: "inline-block",
                    marginTop: "8px",
                  }}
                >
                  Forgot Password?
                </Link>
              </div>
              <button
                type="submit"
                className="kkb-btn kkb-btn--primary kkb-btn--full"
                disabled={loading}
              >
                {loading ? (
                  "Signing in…"
                ) : (
                  <>
                    <span>Sign In</span>
                    <FiArrowRight size={14} />
                  </>
                )}
              </button>
              <div className="kkb-auth__ssl">
                <div className="kkb-auth__ssl-line" />
                <FiShield size={12} />
                <span>256-bit SSL · Your data is safe</span>
                <div className="kkb-auth__ssl-line" />
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="kkb-auth__form">
              <h2 className="kkb-auth__title">Create account</h2>
              <p className="kkb-auth__subtitle">
                Join for exclusive offers & rewards
              </p>

              {[
                {
                  label: "Full Name",
                  key: "name",
                  type: "text",
                  ph: "Your full name",
                },
                {
                  label: "Email Address",
                  key: "email",
                  type: "email",
                  ph: "you@email.com",
                },
                {
                  label: "Phone Number",
                  key: "phone",
                  type: "tel",
                  ph: "9843961449",
                },
                {
                  label: "Password",
                  key: "password",
                  type: "password",
                  ph: "Create a strong password",
                },
              ].map((f) => (
                <div key={f.key} className="kkb-form__group">
                  <label className="kkb-form__label">{f.label}</label>
                  <input
                    className="kkb-form__input"
                    type={f.type}
                    value={rForm[f.key]}
                    onChange={(e) =>
                      setRForm((p) => ({ ...p, [f.key]: e.target.value }))
                    }
                    placeholder={f.ph}
                    required={f.key !== "phone"}
                  />
                </div>
              ))}

              <button
                type="submit"
                className="kkb-btn kkb-btn--primary kkb-btn--full"
                disabled={loading}
              >
                {loading ? (
                  "Creating account…"
                ) : (
                  <>
                    <span>Create Account</span>
                    <FiArrowRight size={14} />
                  </>
                )}
              </button>

              <p className="kkb-auth__terms">
                By creating an account, you agree to our <a href="#">Terms</a> &{" "}
                <a href="#">Privacy Policy</a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────
function Dashboard() {
  const { user, logout, cartCount, wishlist, showToast } = useApp();
  const [activeTab, setActiveTab] = useState("overview");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [wlLoading, setWlLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
  });
  const [pwForm, setPwForm] = useState({
    currentPassword: "",
    newPassword: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState({ curr: false, new: false });
  const [addrForm, setAddrForm] = useState({
    type: "Home",
    name: "",
    address: "",
    city: "",
    state: "Tamil Nadu",
    pincode: "",
    phone: "",
    isDefault: false,
  });
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [editAddrId, setEditAddrId] = useState(null);

  const navItems = [
    { key: "overview", icon: FiPieChart, text: "Overview" },
    {
      key: "orders",
      icon: FiPackage,
      text: "My Orders",
      badge: orders.length || null,
    },
    {
      key: "wishlist",
      icon: FiHeart,
      text: "Wishlist",
      badge: wishlist.length || null,
    },
    { key: "addresses", icon: FiMapPin, text: "Addresses" },
    { key: "profile", icon: FiUser, text: "Profile" },
  ];

  useEffect(() => {
    setOrdersLoading(true);
    api.orders
      .myOrders()
      .then((d) => setOrders(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setOrdersLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === "wishlist") {
      setWlLoading(true);
      api.wishlist
        .get()
        .then((d) => setWishlistProducts(d.products || []))
        .catch(() => {})
        .finally(() => setWlLoading(false));
    }
    if (activeTab === "addresses") {
      setAddrLoading(true);
      api.users
        .getAddresses()
        .then((d) => setAddresses(Array.isArray(d) ? d : []))
        .catch(() => {})
        .finally(() => setAddrLoading(false));
    }
  }, [activeTab]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.users.updateProfile(profile);
      showToast("Profile updated!");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.auth.updatePassword(pwForm);
      showToast("Password changed!");
      setPwForm({ currentPassword: "", newPassword: "" });
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    try {
      const updated = editAddrId
        ? await api.users.updateAddress(editAddrId, addrForm)
        : await api.users.addAddress(addrForm);
      setAddresses(Array.isArray(updated) ? updated : []);
      setShowAddrForm(false);
      setEditAddrId(null);
      setAddrForm({
        type: "Home",
        name: "",
        address: "",
        city: "",
        state: "Tamil Nadu",
        pincode: "",
        phone: "",
        isDefault: false,
      });
      showToast(editAddrId ? "Address updated!" : "Address added!");
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      const updated = await api.users.deleteAddress(id);
      setAddresses(Array.isArray(updated) ? updated : []);
      showToast("Address deleted.");
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const startEditAddr = (addr) => {
    setAddrForm({
      type: addr.type,
      name: addr.name,
      address: addr.address,
      city: addr.city,
      state: addr.state,
      pincode: addr.pincode,
      phone: addr.phone,
      isDefault: addr.isDefault,
    });
    setEditAddrId(addr._id);
    setShowAddrForm(true);
  };

  const resetAddrForm = () => {
    setShowAddrForm(false);
    setEditAddrId(null);
    setAddrForm({
      type: "Home",
      name: "",
      address: "",
      city: "",
      state: "Tamil Nadu",
      pincode: "",
      phone: "",
      isDefault: false,
    });
  };

  return (
    <div className="kkb-page">
      {/* ── Hero Banner ── */}
      <div className="kkb-heroap">
        <div className="kkb-hero__innerap">
          <div className="kkb-hero__left">
            <div className="kkb-hero__avatar">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="kkb-hero__info">
              <div className="kkb-hero__greeting">Welcome back</div>
              <div className="kkb-hero__name">{user?.name}</div>
              <div className="kkb-hero__email">{user?.email}</div>
            </div>
          </div>
          <button onClick={logout} className="kkb-hero__logout">
            <FiLogOut size={14} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* ── Dashboard Grid ── */}
      <div className="kkb-dashboard">
        {/* ── Sidebar ── */}
        <aside className="kkb-sidebar">
          <span className="kkb-sidebar__label">Account</span>
          <nav className="kkb-sidebar__nav">
            {navItems.map(({ key, icon: Icon, text, badge }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`kkb-sidebar__item ${activeTab === key ? "kkb-sidebar__item--active" : ""}`}
              >
                <Icon size={16} />
                <span>{text}</span>
                {badge > 0 && (
                  <span className="kkb-sidebar__badge">{badge}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="kkb-sidebar__member">
            <FiAward size={14} className="kkb-sidebar__member-icon" />
            <div>
              <div className="kkb-sidebar__member-label">Member Since</div>
              <div className="kkb-sidebar__member-date">
                {new Date(user?.createdAt).toLocaleDateString("en-IN", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="kkb-content">
          {/* ── Overview ── */}
          {activeTab === "overview" && (
            <div className="kkb-sectionap">
              <div className="kkb-stats">
                {[
                  {
                    icon: FiPackage,
                    label: "Total Orders",
                    value: ordersLoading ? "—" : orders.length,
                  },
                  {
                    icon: FiHeart,
                    label: "Wishlist Items",
                    value: wishlist.length,
                  },
                  {
                    icon: FiShoppingCart,
                    label: "Cart Items",
                    value: cartCount,
                  },
                  {
                    icon: FiClock,
                    label: "Last Order",
                    value: orders[0]
                      ? new Date(orders[0].createdAt).toLocaleDateString(
                          "en-IN",
                          { day: "2-digit", month: "short" },
                        )
                      : "None",
                  },
                ].map((s, i) => (
                  <div key={i} className="kkb-stats__card">
                    <div className="kkb-stats__icon">
                      <s.icon />
                    </div>
                    <div>
                      <div className="kkb-stats__label">{s.label}</div>
                      <div className="kkb-stats__value">{s.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="kkb-section__head">
                <span className="kkb-section__title">Recent Orders</span>
                <button
                  onClick={() => setActiveTab("orders")}
                  className="kkb-btn kkb-btn--link"
                >
                  View all <FiArrowRight size={12} />
                </button>
              </div>

              <div className="kkb-section__body">
                {ordersLoading ? (
                  <p className="kkb-loading">Loading orders…</p>
                ) : orders.length === 0 ? (
                  <div className="kkb-empty">
                    <div className="kkb-empty__icon">
                      <FiPackage size={28} />
                    </div>
                    <p className="kkb-empty__text">No orders yet</p>
                    <Link to="/collection" className="kkb-btn kkb-btn--primary">
                      Start Shopping
                    </Link>
                  </div>
                ) : (
                  orders.slice(0, 3).map((order) => (
                    <div key={order.id} className="kkb-order">
                      <div className="kkb-order__head">
                        <div>
                          <div className="kkb-order__id">#{order.orderId}</div>
                          <div className="kkb-order__date">
                            {new Date(order.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </div>
                        </div>
                        <span
                          className={`kkb-status kkb-status--${order.orderStatus?.toLowerCase()}`}
                        >
                          <span className="kkb-status__dot" />
                          {order.orderStatus}
                        </span>
                      </div>
                      <div className="kkb-order__track">
                        <OrderProgress status={order.orderStatus} />
                      </div>
                      <div className="kkb-order__foot">
                        <span className="kkb-order__items-count">
                          {order.items?.length} item(s)
                        </span>
                        <span className="kkb-order__total">
                          ₹{order.totalAmount?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Orders ── */}
          {activeTab === "orders" && (
            <div className="kkb-section">
              <div className="kkb-section__head">
                <span className="kkb-section__title">My Orders</span>
                <span className="kkb-section__count">
                  {orders.length} total
                </span>
              </div>
              <div className="kkb-section__body">
                {ordersLoading ? (
                  <p className="kkb-loading">Loading…</p>
                ) : orders.length === 0 ? (
                  <div className="kkb-empty">
                    <div className="kkb-empty__icon">
                      <FiPackage size={28} />
                    </div>
                    <p className="kkb-empty__text">No orders yet</p>
                    <Link to="/collection" className="kkb-btn kkb-btn--primary">
                      Start Shopping
                    </Link>
                  </div>
                ) : (
                  orders.map((order) => (
                    <div key={order.id} className="kkb-order">
                      <div className="kkb-order__head">
                        <div>
                          <div className="kkb-order__id">
                            Order #{order.orderId}
                          </div>
                          <div className="kkb-order__date">
                            {new Date(order.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              },
                            )}
                          </div>
                        </div>
                        <span
                          className={`kkb-status kkb-status--${order.orderStatus?.toLowerCase()}`}
                        >
                          <span className="kkb-status__dot" />
                          {order.orderStatus}
                        </span>
                      </div>

                      <div className="kkb-order__track">
                        <OrderProgress status={order.orderStatus} />
                      </div>

                      <div className="kkb-order__meta">
                        <div className="kkb-order__meta-left">
                          <span>{order.items?.length} item(s)</span>
                          {order.trackingNumber && (
                            <span className="kkb-order__tracking">
                              <FiTruck size={12} /> {order.trackingNumber}
                            </span>
                          )}
                        </div>
                        <span className="kkb-order__total">
                          ₹{order.totalAmount?.toLocaleString()}
                        </span>
                      </div>

                      <div className="kkb-order__actions">
                        <Link
                          to="/track"
                          className="kkb-btn kkb-btn--ghost kkb-btn--sm"
                        >
                          <FiTruck size={13} /> Track
                        </Link>
                        <button
                          className="kkb-btn kkb-btn--ghost kkb-btn--sm"
                          onClick={() =>
                            setExpandedOrderId(
                              expandedOrderId === order.id ? null : order.id,
                            )
                          }
                        >
                          {expandedOrderId === order.id ? (
                            <>
                              <FiChevronUp size={13} /> Hide Items
                            </>
                          ) : (
                            <>
                              <FiChevronDown size={13} /> View Items
                            </>
                          )}
                        </button>
                        {["Processing", "Confirmed"].includes(
                          order.orderStatus,
                        ) && (
                          <button
                            className="kkb-btn kkb-btn--danger kkb-btn--sm"
                            onClick={async () => {
                              const reason = window.prompt(
                                "Reason for cancellation:",
                              );
                              if (!reason) return;
                              try {
                                const updated = await api.orders.cancel(
                                  order.id,
                                  reason,
                                );
                                setOrders((prev) =>
                                  prev.map((o) =>
                                    o.id === order.id ? updated : o,
                                  ),
                                );
                                showToast("Order cancelled.");
                              } catch (err) {
                                showToast(err.message, "error");
                              }
                            }}
                          >
                            <FiX size={13} /> Cancel
                          </button>
                        )}
                      </div>

                      {expandedOrderId === order.id && (
                        <div className="kkb-order__panel">
                          <div className="kkb-order__panel-title">
                            Order Items
                          </div>
                          {order.items?.map((item, idx) => (
                            <div
                              key={idx}
                              className={`kkb-order__row ${idx === order.items.length - 1 ? "kkb-order__row--last" : ""}`}
                            >
                              <div>
                                <div className="kkb-order__row-name">
                                  {item.name}
                                </div>
                                <div className="kkb-order__row-meta">
                                  {item.size ? `Size: ${item.size} · ` : ""}Qty:{" "}
                                  {item.qty}
                                </div>
                              </div>
                              <div className="kkb-order__row-price">
                                ₹{(item.price * item.qty).toLocaleString()}
                              </div>
                            </div>
                          ))}
                          <div className="kkb-order__panel-total">
                            <span>Total</span>
                            <span className="kkb-order__panel-total-amt">
                              ₹{order.totalAmount?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Wishlist ── */}
          {activeTab === "wishlist" && (
            <div className="kkb-section">
              <div className="kkb-section__head">
                <span className="kkb-section__title">Wishlist</span>
                <span className="kkb-section__count">
                  {wishlist.length} items
                </span>
              </div>
              <div className="kkb-section__body">
                {wlLoading ? (
                  <p className="kkb-loading">Loading…</p>
                ) : wishlistProducts.length === 0 ? (
                  <div className="kkb-empty">
                    <div className="kkb-empty__icon">
                      <FiHeart size={28} />
                    </div>
                    <p className="kkb-empty__text">Your wishlist is empty</p>
                    <Link to="/collection" className="kkb-btn kkb-btn--primary">
                      Browse Collection
                    </Link>
                  </div>
                ) : (
                  <div className="kkb-wishlist">
                    {wishlistProducts.map((product) => (
                      <div key={product._id} className="kkb-wishlist__card">
                        <img
                          src={`${BASE_URL}${product.image}`}
                          alt={product.name}
                          className="kkb-wishlist__img"
                          onError={(e) => {
                            e.target.src =
                              "https://placehold.co/300x220/f4f1eb/8B6914?text=KKB";
                          }}
                        />
                        <div className="kkb-wishlist__info">
                          <div className="kkb-wishlist__name">
                            {product.name}
                          </div>
                          <div className="kkb-wishlist__price">
                            ₹{product.price?.toLocaleString()}
                          </div>
                          <Link
                            to={`/product/${product.id}`}
                            className="kkb-btn kkb-btn--primary kkb-btn--sm"
                          >
                            View Product <FiArrowRight size={12} />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Addresses ── */}
          {activeTab === "addresses" && (
            <div className="kkb-section">
              <div className="kkb-section__head">
                <span className="kkb-section__title">Saved Addresses</span>
                <button
                  className="kkb-btn kkb-btn--primary"
                  onClick={() => {
                    setShowAddrForm(true);
                    setEditAddrId(null);
                  }}
                >
                  <FiPlus size={14} /> Add Address
                </button>
              </div>
              <div className="kkb-section__body">
                {showAddrForm && (
                  <div className="kkb-addr-form">
                    <div className="kkb-addr-form__title">
                      {editAddrId ? "Edit Address" : "New Address"}
                    </div>
                    <form onSubmit={handleSaveAddress}>
                      <div className="kkb-addr-form__grid">
                        {[
                          { label: "Type", key: "type", ph: "Home / Office" },
                          {
                            label: "Full Name",
                            key: "name",
                            ph: "Recipient name",
                          },
                          { label: "Phone", key: "phone", ph: "Phone number" },
                          { label: "City", key: "city", ph: "City" },
                          { label: "State", key: "state", ph: "State" },
                          { label: "Pincode", key: "pincode", ph: "Pincode" },
                        ].map((f) => (
                          <div key={f.key} className="kkb-form__group">
                            <label className="kkb-form__label">{f.label}</label>
                            <input
                              className="kkb-form__input"
                              value={addrForm[f.key]}
                              onChange={(e) =>
                                setAddrForm((p) => ({
                                  ...p,
                                  [f.key]: e.target.value,
                                }))
                              }
                              placeholder={f.ph}
                              required
                            />
                          </div>
                        ))}
                      </div>
                      <div className="kkb-form__group">
                        <label className="kkb-form__label">
                          Street Address
                        </label>
                        <input
                          className="kkb-form__input"
                          value={addrForm.address}
                          onChange={(e) =>
                            setAddrForm((p) => ({
                              ...p,
                              address: e.target.value,
                            }))
                          }
                          placeholder="House no, Street, Locality"
                          required
                        />
                      </div>
                      <label className="kkb-form__checkbox">
                        <input
                          type="checkbox"
                          checked={addrForm.isDefault}
                          onChange={(e) =>
                            setAddrForm((p) => ({
                              ...p,
                              isDefault: e.target.checked,
                            }))
                          }
                        />
                        <span>Set as default address</span>
                      </label>
                      <div className="kkb-addr-form__actions">
                        <button
                          type="submit"
                          className="kkb-btn kkb-btn--primary"
                        >
                          <FiCheck size={14} /> Save Address
                        </button>
                        <button
                          type="button"
                          className="kkb-btn kkb-btn--ghost"
                          onClick={resetAddrForm}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {addrLoading ? (
                  <p className="kkb-loading">Loading…</p>
                ) : addresses.length === 0 && !showAddrForm ? (
                  <div className="kkb-empty">
                    <div className="kkb-empty__icon">
                      <FiMapPin size={28} />
                    </div>
                    <p className="kkb-empty__text">No saved addresses</p>
                  </div>
                ) : (
                  <div className="kkb-addr-grid">
                    {addresses.map((addr) => (
                      <div key={addr._id} className="kkb-addr-card">
                        <div className="kkb-addr-card__head">
                          <div className="kkb-addr-card__type">
                            {addr.type === "Home" ? (
                              <FiHome size={13} />
                            ) : (
                              <FiBriefcase size={13} />
                            )}
                            {addr.type}
                          </div>
                          {addr.isDefault && (
                            <span className="kkb-addr-card__badge">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="kkb-addr-card__body">
                          <strong>{addr.name}</strong>
                          <span>{addr.address}</span>
                          <span>
                            {addr.city}, {addr.state} – {addr.pincode}
                          </span>
                          <span className="kkb-addr-card__phone">
                            {addr.phone}
                          </span>
                        </div>
                        <div className="kkb-addr-card__actions">
                          <button
                            className="kkb-btn kkb-btn--ghost kkb-btn--sm"
                            onClick={() => startEditAddr(addr)}
                          >
                            <FiEdit2 size={12} /> Edit
                          </button>
                          <button
                            className="kkb-btn kkb-btn--danger kkb-btn--sm"
                            onClick={() => handleDeleteAddress(addr._id)}
                          >
                            <FiTrash2 size={12} /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Profile ── */}
          {activeTab === "profile" && (
            <div className="kkb-profile">
              <div className="kkb-section">
                <div className="kkb-section__head">
                  <span className="kkb-section__title">Profile Settings</span>
                </div>
                <div className="kkb-section__body">
                  <form onSubmit={handleSaveProfile}>
                    <div className="kkb-profile__grid">
                      <div className="kkb-form__group">
                        <label className="kkb-form__label">Full Name</label>
                        <input
                          className="kkb-form__input"
                          value={profile.name}
                          onChange={(e) =>
                            setProfile((p) => ({ ...p, name: e.target.value }))
                          }
                          required
                        />
                      </div>
                      <div className="kkb-form__group">
                        <label className="kkb-form__label">Phone Number</label>
                        <input
                          className="kkb-form__input"
                          type="tel"
                          value={profile.phone}
                          onChange={(e) =>
                            setProfile((p) => ({ ...p, phone: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="kkb-form__group">
                      <label className="kkb-form__label">Email Address</label>
                      <input
                        className="kkb-form__input kkb-form__input--disabled"
                        type="email"
                        value={user?.email}
                        disabled
                      />
                      <p className="kkb-form__hint">
                        Email cannot be changed for security reasons.
                      </p>
                    </div>
                    <button
                      type="submit"
                      className="kkb-btn kkb-btn--primary"
                      disabled={saving}
                    >
                      {saving ? (
                        "Saving…"
                      ) : (
                        <>
                          <FiCheck size={14} /> Save Changes
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>

              <div className="kkb-section">
                <div className="kkb-section__head">
                  <span className="kkb-section__title">Change Password</span>
                  <span className="kkb-section__secured">
                    <FiShield size={13} /> Secured
                  </span>
                </div>
                <div className="kkb-section__body">
                  <form onSubmit={handleChangePassword}>
                    {[
                      {
                        label: "Current Password",
                        key: "currentPassword",
                        show: showPw.curr,
                        toggle: () =>
                          setShowPw((p) => ({ ...p, curr: !p.curr })),
                      },
                      {
                        label: "New Password",
                        key: "newPassword",
                        show: showPw.new,
                        toggle: () => setShowPw((p) => ({ ...p, new: !p.new })),
                      },
                    ].map((f) => (
                      <div key={f.key} className="kkb-form__group">
                        <label className="kkb-form__label">{f.label}</label>
                        <div className="kkb-form__input-wrap">
                          <input
                            className="kkb-form__input"
                            type={f.show ? "text" : "password"}
                            value={pwForm[f.key]}
                            onChange={(e) =>
                              setPwForm((p) => ({
                                ...p,
                                [f.key]: e.target.value,
                              }))
                            }
                            required
                          />
                          <button
                            type="button"
                            className="kkb-form__eye"
                            onClick={f.toggle}
                          >
                            {f.show ? (
                              <FiEyeOff size={16} />
                            ) : (
                              <FiEye size={16} />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="submit"
                      className="kkb-btn kkb-btn--primary"
                      disabled={saving}
                    >
                      {saving ? (
                        "Updating…"
                      ) : (
                        <>
                          <FiShield size={14} /> Update Password
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ─── Email Verification Component ───────────────────────────────────────────

function EmailVerificationPage() {
  console.log("🔍 EmailVerificationPage component is rendering");
  console.log("Current URL:", window.location.href);
  const { token } = useParams();
  console.log("📝 Token from URL params:", token);
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    console.log("🔄 useEffect running, token:", token);
    if (token) {
      verifyEmail();
    } else {
      console.log("❌ No token found in URL params");
      setStatus("error");
      setMessage("No verification token provided.");
    }
  }, [token]);

  const verifyEmail = async () => {
    console.log("📧 Starting email verification with token:", token);
    try {
      console.log("Verifying email with token:", token);
      const response = await api.auth.verifyEmail(token);
      console.log("Verification response:", response);

      setStatus("success");
      setMessage(
        response.message || "Email verified successfully! You can now login.",
      );

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/account");
      }, 3000);
    } catch (err) {
      console.error("Verification error:", err);
      setStatus("error");
      setMessage(
        err.message ||
          "Failed to verify email. The link may be expired or invalid.",
      );
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      alert("Please enter your email address");
      return;
    }

    setResending(true);
    try {
      await api.auth.resendVerification(resendEmail);
      setStatus("resent");
      setMessage(
        "Verification email has been resent. Please check your inbox.",
      );
    } catch (err) {
      alert(err.message || "Failed to resend verification email");
    } finally {
      setResending(false);
    }
  };

  if (status === "verifying") {
    return (
      <div className="kkb-verification">
        <div className="kkb-verification__container">
          <div className="kkb-verification__loader">
            <div className="kkb-spinner"></div>
          </div>
          <h2>Verifying your email...</h2>
          <p>Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="kkb-verification">
        <div className="kkb-verification__container kkb-verification__container--success">
          <div className="kkb-verification__icon">
            <FiCheck size={48} />
          </div>
          <h2>Email Verified!</h2>
          <p>{message}</p>
          <p>Redirecting to login page...</p>
          <Link to="/account" className="kkb-btn kkb-btn--primary">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="kkb-verification">
        <div className="kkb-verification__container kkb-verification__container--error">
          <div className="kkb-verification__icon">
            <FiX size={48} />
          </div>
          <h2>Verification Failed</h2>
          <p>{message}</p>

          <div className="kkb-verification__resend">
            <h3>Request New Verification Link</h3>
            <form onSubmit={handleResendVerification}>
              <div className="kkb-form__group">
                <label className="kkb-form__label">Email Address</label>
                <input
                  type="email"
                  className="kkb-form__input"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  required
                />
              </div>
              <button
                type="submit"
                className="kkb-btn kkb-btn--primary"
                disabled={resending}
              >
                <FiRefreshCw size={14} />
                {resending ? "Sending..." : "Resend Verification Email"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (status === "resent") {
    return (
      <div className="kkb-verification">
        <div className="kkb-verification__container kkb-verification__container--success">
          <div className="kkb-verification__icon">
            <FiMail size={48} />
          </div>
          <h2>Verification Email Sent!</h2>
          <p>{message}</p>
          <p>Please check your inbox and click the verification link.</p>
          <Link to="/account" className="kkb-btn kkb-btn--primary">
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return null;
}

// ─── Page Export ─────────────────────────────────────────────────────────────
export default function AccountPage() {
  const { user, authLoading } = useApp();

  if (authLoading)
    return (
      <div className="kkb-pageload">
        <span className="kkb-pageload__text">Loading…</span>
      </div>
    );

  return user ? <Dashboard /> : <AuthForm />;
}

// Export the verification page separately for routing
export { EmailVerificationPage };
